import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  buildConditionSchema,
  getAllowedConditionEntityTypesForMode,
  getFieldsForEntityType,
  getFieldTypeByFieldName,
  getOperatorsForFieldType,
  WorkflowConditionMode,
} from "@/pages/workflows/edit/actions/workflow-edit-block-condition-modal.tsx";
import { useDepartmentsTree } from "@/queries/use-departments.query.ts";
import { useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLegalEntities } from "@/queries/use-legal-entities.query.ts";
import { useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import { useJobProfiles } from "@/queries/use-job-profiles.query.ts";
import { useTimeOffCategoriesSearchInline } from "@/queries/use-time-off.query.ts";
import { useStages } from "@/queries/recruit/use-stages.query.ts";
import { usePipelines } from "@/queries/recruit/use-pipelines.query.ts";
import { useDisqualifications } from "@/queries/recruit/use-disqualifications.query.ts";
import { useJobsSearchInline } from "@/queries/recruit/use-jobs.query.ts";
import { ApiIdTitle } from "@/types/common.types.ts";
import {
  ApiWorkflowCondition,
  ApiWorkflowConditionEntityTypes,
  ApiWorkflowConditionFieldType,
  ApiWorkflowConditionFieldTypes,
  ApiWorkflowConditionOperator,
  getApiWorkflowConditionEntityTypesTitle,
} from "@/types/workflows/workflow.condition.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Modal,
  ModalProps,
  Multiselect,
  Select,
  SelectItem,
  SelectValueType,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const CONDITION_MODE_CONFIG: Record<
  WorkflowConditionMode,
  {
    allowedEntityTypes: ApiWorkflowConditionEntityTypes[];
    initialEntityType: ApiWorkflowConditionEntityTypes;
    hideTimeOffFieldOperator: boolean;
  }
> = {
  block: {
    allowedEntityTypes: getAllowedConditionEntityTypesForMode("block"),
    initialEntityType: ApiWorkflowConditionEntityTypes.employee,
    hideTimeOffFieldOperator: false,
  },
  employee_trigger: {
    allowedEntityTypes: getAllowedConditionEntityTypesForMode("employee_trigger"),
    initialEntityType: ApiWorkflowConditionEntityTypes.employee,
    hideTimeOffFieldOperator: false,
  },
  time_off_trigger: {
    allowedEntityTypes: getAllowedConditionEntityTypesForMode("time_off_trigger"),
    initialEntityType: ApiWorkflowConditionEntityTypes.time_off,
    hideTimeOffFieldOperator: true,
  },
  candidate: {
    allowedEntityTypes: getAllowedConditionEntityTypesForMode("candidate"),
    initialEntityType: ApiWorkflowConditionEntityTypes.candidate,
    hideTimeOffFieldOperator: false,
  },
};

const getDefaultFieldNameByEntityType = (
  entityType: ApiWorkflowConditionEntityTypes
): ApiWorkflowConditionFieldTypes => {
  switch (entityType) {
    case ApiWorkflowConditionEntityTypes.time_off:
      return ApiWorkflowConditionFieldTypes.time_off_category_id;
    case ApiWorkflowConditionEntityTypes.candidate:
      return ApiWorkflowConditionFieldTypes.candidate_stage_id;
    case ApiWorkflowConditionEntityTypes.job:
      return ApiWorkflowConditionFieldTypes.job_id;
    default:
      return ApiWorkflowConditionFieldTypes.employee_employment_legal_entity_id;
  }
};

export interface WorkflowEditBlockConditionModalProps extends ModalProps {
  condition?: ApiWorkflowCondition;
  mode?: WorkflowConditionMode;
  onSave: (condition: ApiWorkflowCondition) => void;
}

export const WorkflowEditBlockConditionModal = ({
  condition,
  mode = "block",
  onSave,
  ...props
}: WorkflowEditBlockConditionModalProps) => {
  const { close } = useModals();
  const { id } = props;
  const { t } = useTranslation();
  const modeConfig = CONDITION_MODE_CONFIG[mode];

  const conditionToEdit = useMemo(() => {
    if (!condition)
      return {
        id: 0,
        entity_type: modeConfig.initialEntityType,
        field_name: getDefaultFieldNameByEntityType(modeConfig.initialEntityType),
        operator: ApiWorkflowConditionOperator.in_,
        field_value: {
          type: ApiWorkflowConditionFieldType.array_integer,
          value: [],
        },
        condition_group: 1,
        condition_entities: [],
      };
    return condition;
  }, [condition, modeConfig.initialEntityType]);

  const conditionSchema = buildConditionSchema();

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ApiWorkflowCondition>({
    mode: "onSubmit",
    resolver: yupResolver(conditionSchema),
    values: {
      id: conditionToEdit.id,
      entity_type: conditionToEdit.entity_type,
      field_name: conditionToEdit.field_name,
      field_value: conditionToEdit.field_value,
      operator: conditionToEdit.operator,
      condition_group: conditionToEdit.condition_group,
      condition_entities: conditionToEdit.condition_entities,
    },
  });

  const entityType = watch("entity_type");
  const fieldName = watch("field_name");
  const operator = watch("operator");
  const fieldValueType = watch("field_value.type");
  const conditionEntities = watch("condition_entities");

  const isEmployeeEntityType = entityType == ApiWorkflowConditionEntityTypes.employee;
  const isTimeOffEntityType = entityType == ApiWorkflowConditionEntityTypes.time_off;
  const isCandidateEntityType = entityType == ApiWorkflowConditionEntityTypes.candidate;
  const isJobEntityType = entityType == ApiWorkflowConditionEntityTypes.job;
  const hideFieldAndOperator = modeConfig.hideTimeOffFieldOperator && isTimeOffEntityType;

  const { data: legalEntities, isLoading: isLoadingLegalEntities } =
    useLegalEntities(isEmployeeEntityType);
  const { data: departments, isLoading: isLoadingDepartments } = useDepartmentsTree(
    isEmployeeEntityType || isJobEntityType
  );
  const divisions = useDivisionsSearchInline(isEmployeeEntityType || isJobEntityType);
  const { options: jobProfileOptions, isLoading: isLoadingJobProfiles } = useJobProfiles(
    true,
    isEmployeeEntityType &&
      fieldName === ApiWorkflowConditionFieldTypes.employee_position_job_profile_id
  );
  const locations = useLocationsSearchInline(isEmployeeEntityType || isJobEntityType);
  const timeOffCategories = useTimeOffCategoriesSearchInline({
    enabled: entityType == ApiWorkflowConditionEntityTypes.time_off,
  });
  const { data: stages, isLoading: isLoadingStages } = useStages(isCandidateEntityType);
  const { data: pipelines, isLoading: isLoadingPipelines } = usePipelines(isCandidateEntityType);
  const { data: disqualifications, isLoading: isLoadingDisqualifications } =
    useDisqualifications(isCandidateEntityType);
  const jobs = useJobsSearchInline(isJobEntityType);

  const entityTypeOptions: ApiIdTitle[] = useMemo(
    () =>
      modeConfig.allowedEntityTypes.map((type) => ({
        id: type,
        title: getApiWorkflowConditionEntityTypesTitle(type),
      })),
    [modeConfig.allowedEntityTypes]
  );
  const availableFields = useMemo(() => getFieldsForEntityType(entityType), [entityType]);
  const availableOperators = useMemo(
    () => getOperatorsForFieldType(fieldValueType),
    [fieldValueType]
  );

  const handleFieldNameChange = (newFieldName: ApiWorkflowConditionFieldTypes) => {
    setValue("field_name", newFieldName);
    const newFieldType = getFieldTypeByFieldName(newFieldName);
    setValue("field_value.type", newFieldType);
    setValue("field_value.value", null);
    setValue("condition_entities", []);
  };

  useEffect(() => {
    if (!modeConfig.hideTimeOffFieldOperator) return;

    const availableOptions = getFieldsForEntityType(entityType);
    const isCurrentFieldAllowed = availableOptions.some((option) => option.id === fieldName);
    if (!isCurrentFieldAllowed && availableOptions.length > 0) {
      const firstField = availableOptions[0].id as ApiWorkflowConditionFieldTypes;
      setValue("field_name", firstField);
      setValue("field_value.type", getFieldTypeByFieldName(firstField));
      setValue("field_value.value", null);
      setValue("condition_entities", []);
    }
  }, [modeConfig.hideTimeOffFieldOperator, entityType, fieldName, setValue]);

  const shouldShowValueInput = ![
    ApiWorkflowConditionOperator.is_null,
    ApiWorkflowConditionOperator.is_not_null,
  ].includes(operator);

  const renderValueInput = () => {
    if (!shouldShowValueInput) return null;

    if (
      fieldValueType === ApiWorkflowConditionFieldType.array_integer ||
      fieldValueType === ApiWorkflowConditionFieldType.array_string
    ) {
      let options: ApiIdTitle[] = [];
      let searchOptions: ApiIdTitle[] | undefined = undefined;
      let onSearchOptions: ((text: string | undefined) => void) | undefined = undefined;
      let loadingState = false;

      if (fieldName === ApiWorkflowConditionFieldTypes.employee_employment_legal_entity_id) {
        options = legalEntities?.map((le) => ({ id: le.id, title: le.title })) || [];
        loadingState = isLoadingLegalEntities;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.employee_position_department_id) {
        options = (departments as ApiIdTitle[]) || [];
        loadingState = isLoadingDepartments;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.employee_position_division_id) {
        options = (divisions.options as ApiIdTitle[]) || [];
        searchOptions = options;
        onSearchOptions = divisions.search;
        loadingState = divisions.isLoading;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.employee_position_job_profile_id) {
        options = jobProfileOptions;
        loadingState = isLoadingJobProfiles;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.employee_position_location_id) {
        options = (locations.options as ApiIdTitle[]) || [];
        searchOptions = options;
        onSearchOptions = locations.search;
        loadingState = locations.isLoading;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.time_off_category_id) {
        options = (timeOffCategories.options as ApiIdTitle[]) || [];
        searchOptions = options;
        onSearchOptions = timeOffCategories.search;
        loadingState = timeOffCategories.isLoading;
      } else if (
        fieldName === ApiWorkflowConditionFieldTypes.candidate_stage_id ||
        fieldName === ApiWorkflowConditionFieldTypes.candidate_previous_stage_id
      ) {
        options = stages?.map((stage) => ({ id: stage.id, title: stage.title })) || [];
        loadingState = isLoadingStages;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.candidate_pipeline_id) {
        options = pipelines?.map((pipeline) => ({ id: pipeline.id, title: pipeline.title })) || [];
        loadingState = isLoadingPipelines;
      } else if (
        fieldName === ApiWorkflowConditionFieldTypes.candidate_disqualification_reason_id
      ) {
        options =
          disqualifications?.map((reason) => ({ id: reason.id, title: reason.title })) || [];
        loadingState = isLoadingDisqualifications;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.job_id) {
        options = (jobs.options as ApiIdTitle[]) || [];
        searchOptions = options;
        onSearchOptions = jobs.search;
        loadingState = jobs.isLoading;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.job_department_id) {
        options = (departments as ApiIdTitle[]) || [];
        loadingState = isLoadingDepartments;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.job_division_id) {
        options = (divisions.options as ApiIdTitle[]) || [];
        searchOptions = options;
        onSearchOptions = divisions.search;
        loadingState = divisions.isLoading;
      } else if (fieldName === ApiWorkflowConditionFieldTypes.job_location_id) {
        options = (locations.options as ApiIdTitle[]) || [];
        searchOptions = options;
        onSearchOptions = locations.search;
        loadingState = locations.isLoading;
      }

      const initialConditionEntities =
        conditionToEdit.entity_type === entityType && conditionToEdit.field_name === fieldName
          ? conditionToEdit.condition_entities
          : [];
      const selectedConditionEntities = (conditionEntities as ApiIdTitle[]) || [];
      if (initialConditionEntities.length > 0 || selectedConditionEntities.length > 0) {
        const optionsMap = new Map(
          [...initialConditionEntities, ...options].map((item) => [item.id, item])
        );
        selectedConditionEntities.forEach((item) => {
          if (!optionsMap.has(item.id)) optionsMap.set(item.id, item);
        });
        options = Array.from(optionsMap.values());
      }

      return (
        <Controller
          control={control}
          name="field_value.value"
          render={({ field: { value, onChange } }) => (
            <Multiselect
              label={isTimeOffEntityType ? t("time_off:category") : t("workflow:condition_value")}
              emptyTitle={t("common:select")}
              values={(value as SelectValueType[]) ?? []}
              options={options}
              searchOptions={searchOptions}
              onSearchOptions={onSearchOptions}
              loadOptionsOnOpen={Boolean(onSearchOptions)}
              onChange={(selected, entities) => {
                handleSelect(selected, entities);
                onChange(selected);
              }}
              isLoading={loadingState}
              searchable={true}
              showCaption={
                fieldName === ApiWorkflowConditionFieldTypes.employee_position_job_profile_id
              }
              captionPerItem={
                fieldName === ApiWorkflowConditionFieldTypes.employee_position_job_profile_id
              }
              required={true}
              error={errors.field_value?.value?.message}
            />
          )}
        />
      );
    }

    return null;
  };

  const handleSelect = (_: SelectValueType[], entities: SelectItem[]) => {
    setValue("condition_entities", entities);
  };

  const handleSave = handleSubmit(async (data) => {
    onSave({
      ...data,
    });
    close(id);
  });

  return (
    <Modal
      {...props}
      variant={"middle"}
      title={t("workflow:condition")}
      layout={"base"}
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} className={"w-full"} type={"submit"} onClick={handleSave}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={handleSave}>
        <Controller
          control={control}
          name="entity_type"
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("workflow:condition_entity_type")}
              emptyTitle={t("common:select")}
              value={value}
              options={entityTypeOptions}
              onChange={(e) => {
                onChange(e);
                const availableOptions = getFieldsForEntityType(
                  e as ApiWorkflowConditionEntityTypes
                );
                if (availableOptions.length > 0) {
                  handleFieldNameChange(availableOptions[0].id as ApiWorkflowConditionFieldTypes);
                  if (e === ApiWorkflowConditionEntityTypes.time_off) {
                    setValue("operator", ApiWorkflowConditionOperator.in_);
                  }
                }
              }}
              required={true}
              error={errors.entity_type?.message}
            />
          )}
        />
        {!hideFieldAndOperator && (
          <Controller
            control={control}
            name="field_name"
            render={({ field: { value } }) => (
              <Select
                label={t("workflow:condition_field")}
                emptyTitle={t("common:select")}
                value={value}
                options={availableFields}
                onChange={(e) => handleFieldNameChange(e as ApiWorkflowConditionFieldTypes)}
                required={true}
                error={errors.field_name?.message}
              />
            )}
          />
        )}
        {!hideFieldAndOperator && (
          <Controller
            control={control}
            name="operator"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("workflow:condition_operator")}
                emptyTitle={t("common:select")}
                value={value}
                options={availableOperators}
                onChange={onChange}
                required={true}
                error={errors.operator?.message}
              />
            )}
          />
        )}

        {renderValueInput()}
      </form>
    </Modal>
  );
};
